/**
 * Invoice billing contacts (Oct 2026).
 *
 *   main        — the Main Billing Contact. Their name (and email / phone
 *                 when on file) prints in the invoice's "Bill To" box.
 *   additional  — Additional Billing Contacts: people who should get a
 *                 copy of the invoice by email. Not printed on the PDF.
 *                 Stored now so the portal can email them later; for now
 *                 the invoice is emailed to them by hand.
 *
 * Saved on the invoice as BillingContactsJson:
 *   { main: { name, title, email, phone } | null,
 *     additional: [{ name, title, email }] }
 * The main contact's name is also kept in contactNames (ContactNamesJson)
 * so older readers keep working.
 */

const clean = (v) => String(v || '').trim();

function findContact(contacts, name) {
  const n = clean(name).toLowerCase();
  if (!n) return null;
  return (contacts || []).find((c) => clean(c?.name).toLowerCase() === n) || null;
}

/**
 * Build the stored shape from the picked names, pulling title / email /
 * phone from the supplier's contacts. `previous` (an invoice's existing
 * billingContacts) supplies details for names no longer on the supplier.
 */
export function buildBillingContacts(supplier, mainName, additionalNames, previous) {
  const contacts = Array.isArray(supplier?.contacts) ? supplier.contacts : [];
  const prevMain = previous?.main || null;
  const prevAdditional = Array.isArray(previous?.additional) ? previous.additional : [];

  let main = null;
  if (clean(mainName)) {
    const c = findContact(contacts, mainName);
    const fallback =
      prevMain && clean(prevMain.name).toLowerCase() === clean(mainName).toLowerCase()
        ? prevMain
        : null;
    main = {
      name: clean(mainName),
      title: clean(c?.title || fallback?.title),
      email: clean(c?.email || fallback?.email),
      phone: clean(c?.phone || c?.officePhone || fallback?.phone),
    };
  }

  const seen = new Set(main ? [main.name.toLowerCase()] : []);
  const additional = [];
  for (const raw of additionalNames || []) {
    const name = clean(raw);
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    const c = findContact(contacts, name);
    const fallback = prevAdditional.find((p) => clean(p?.name).toLowerCase() === key);
    additional.push({
      name,
      title: clean(c?.title || fallback?.title),
      email: clean(c?.email || fallback?.email),
    });
  }
  return { main, additional };
}

/**
 * Read an invoice's contacts back into { mainName, additionalNames }.
 * Older invoices (before Oct 2026) only have contactNames: the first is
 * the main contact and the rest become additional contacts.
 */
export function readBillingContacts(invoice) {
  const bc = invoice?.billingContacts;
  if (bc && (bc.main || (Array.isArray(bc.additional) && bc.additional.length))) {
    return {
      mainName: clean(bc.main?.name),
      additionalNames: (bc.additional || []).map((a) => clean(a?.name)).filter(Boolean),
    };
  }
  const names = Array.isArray(invoice?.contactNames) ? invoice.contactNames.map(clean).filter(Boolean) : [];
  return { mainName: names[0] || '', additionalNames: names.slice(1) };
}
